// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable } from '@angular/core';
import { KeyProject, TableDataRow } from '@lfx/key-projects/key-projects.model';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { EcosystemInfluence, EcosystemScoreLevel } from '@lfx/key-projects/entities/ecosystem-influence.entity';

/**
 * Service responsible for transforming KeyProject data into TableDataRow format
 * Follows Clean Architecture: Business logic separated from presentation layer
 */
@Injectable({ providedIn: 'root' })
export class TableDataTransformerService {
  /**
   * Transform an array of KeyProjects into TableDataRows for table display
   */
  transformToTableData(keyProjects: KeyProject[]): TableDataRow[] {
    return keyProjects.map(keyProject => this.transformSingleProject(keyProject));
  }

  /**
   * Transform a single KeyProject into a TableDataRow
   */
  private transformSingleProject(keyProject: KeyProject): TableDataRow {
    return {
      id: keyProject.id,
      projectInfo: {
        name: keyProject.name,
        logoUrl: keyProject.logoUrl,
        slug: keyProject.slug,
        description: keyProject.description,
        isLfProject: keyProject.isLfProject,
      },
      healthScore: this.mapHealthScore(keyProject),
      healthScoreRawData: keyProject.healthScore,
      technicalInfluence: this.capitalizeFirstLetter(keyProject.technicalInfluenceMetrics.technicalInfluenceLevelName),
      technicalInfluencePoints: keyProject.technicalInfluenceMetrics.totalPoints,
      ecosystemInfluence: this.capitalizeFirstLetter(keyProject.ecosystemInfluenceMetrics.ecosystemInfluenceLevelName),
      ecosystemInfluencePoints: keyProject.ecosystemInfluenceMetrics.totalPoints,
      contributors: this.mapContributors(keyProject),
      participants: this.mapParticipants(keyProject),
      activity: this.mapActivity(keyProject),
    };
  }

  /**
   * Map health score to display level
   */
  private mapHealthScore(keyProject: KeyProject) {
    return KeyProjectsHelper.mapHealthScoreToLevel(
      keyProject.healthScore.overallScore
    ).level;
  }

  /**
   * Map ecosystem influence metrics to display level
   */
  private mapEcosystemInfluence(keyProject: KeyProject): EcosystemScoreLevel {
    return EcosystemInfluence.getOverallScoreLevel(
      { influenceLevel: keyProject.ecosystemInfluenceMetrics.ecosystemInfluenceLevel } as any,
      keyProject.isLfProject
    );
  }

  /**
   * Map contributors data for display
   */
  private mapContributors(keyProject: KeyProject) {
    return {
      avatars: keyProject.contributors,
      count: keyProject.contributorCount,
    };
  }

  /**
   * Map participants data for display
   */
  private mapParticipants(keyProject: KeyProject) {
    return {
      avatars: keyProject.participants,
      count: keyProject.participantCount,
    };
  }

  /**
   * Map activity data for display
   */
  private mapActivity(keyProject: KeyProject) {
    return {
      chartData: keyProject.activity.chartData,
      changePercentage: keyProject.activity.changePercentage,
      trend: keyProject.activity.trend,
      period: keyProject.activity.period,
    };
  }

  /**
   * Capitalize the first letter of a string and lowercase the rest
   * Handles null/undefined by returning the original value
   */
  private capitalizeFirstLetter(value: string): string {
    if (!value || typeof value !== 'string') {
      return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}

